/**
 * 「この視聴セッションで回答済みの設問」を端末に覚えておくためのストア。
 *
 * 回答済み設問を再提示しないルール自体は前からあるが、状態がコンポーネントのメモリ上に
 * しか無かったため、**ページを再読み込みすると答えたはずのアンケートがまた出て**いた。
 * とくに `cueTime=0` の必須設問は視聴前ゲートなので、毎回答えないと再生できない。
 *
 * 覚えておく期間は視聴セッション（`moovy_session_id`・30分ローリング）に合わせる。
 * サーバ側の回答重複排除も `sessionId` 単位なので、粒度を揃えておくと
 * 「画面では出ないのに集計では別回答」といったズレが起きない。
 *
 * 単位は**設問ごと**。3問中1問だけ答えて離脱したら、次は残り2問が出る。
 * 動画（紐づけ先）はキーに含めない。同じ視聴セッションのあいだは同じ設問を
 * 動画をまたいで再提示しない、という既存の挙動をそのまま引き継ぐ。
 */

import { getOrCreateSessionId } from './analytics'

const ANSWERED_KEY = 'moovy_survey_answered'

type StoredAnswers = {
  /** 記録した時点の視聴セッションID。変わっていたら期限切れとして捨てる */
  sessionId: string
  questionIds: string[]
}

function readStore(): StoredAnswers | null {
  try {
    const raw = localStorage.getItem(ANSWERED_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as unknown
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      typeof (parsed as StoredAnswers).sessionId !== 'string' ||
      !Array.isArray((parsed as StoredAnswers).questionIds)
    ) {
      return null
    }
    return parsed as StoredAnswers
  } catch (error) {
    console.warn('Failed to read answered survey questions:', error)
    return null
  }
}

/**
 * この視聴セッションで回答済みの設問IDを読み出す。
 * セッションが変わっていれば（30分放置など）空を返す＝またアンケートが出る。
 */
export function loadAnsweredQuestionIds(): Set<string> {
  if (typeof window === 'undefined') return new Set()
  const stored = readStore()
  if (!stored) return new Set()
  if (stored.sessionId !== getOrCreateSessionId()) return new Set()
  return new Set(stored.questionIds)
}

/** 設問を回答済みとして記録する。セッションが変わっていれば作り直す。 */
export function markQuestionAnswered(questionId: string): void {
  if (typeof window === 'undefined') return
  try {
    const sessionId = getOrCreateSessionId()
    const stored = readStore()
    const questionIds =
      stored && stored.sessionId === sessionId ? stored.questionIds : []
    if (questionIds.includes(questionId)) return
    localStorage.setItem(
      ANSWERED_KEY,
      JSON.stringify({ sessionId, questionIds: [...questionIds, questionId] } satisfies StoredAnswers),
    )
  } catch (error) {
    console.warn('Failed to store answered survey question:', error)
  }
}
